import { useEffect } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useVehiclesStore } from '../../stores/useVehiclesStore';
import { useLoadingStore } from '../../stores/useLoadingStore';

import {
    loadVehicles,
    finishVehiclesRequest,
    clearVehiclesRequests,
    isVehiclesRequestActive
} from '../../services/vehiclesService';

import { loadDrivers } from '../../services/driversService';

// =========================
// REALTIME PENDENTE
// =========================

const pendingVehicleEvents = [];

let pendingInvalidate = false;

export function useVehiclesSocket() {

    const socket = useSocketStore(state => state.socket);

    useEffect(() => {

        if (!socket) {
            return;
        }

        const {
            setVehicles,
            upsertVehicle,
            removeVehicle
        } = useVehiclesStore.getState();

        const {
            setLoadProgress
        } = useLoadingStore.getState();

        // =========================
        // PROGRESSO
        // =========================

        const handleProgress = ({
            requestId,
            percentage
        }) => {

            if (!isVehiclesRequestActive(requestId)
            ) {
                return;
            }

            setLoadProgress(percentage ?? 0);

        };

        // =========================
        // DONE
        // =========================

        const handleDone = ({
            requestId,
            veiculos
        }) => {

            const isActive = isVehiclesRequestActive(requestId);

            if (!isActive) {
                return;
            }

            // =========================
            // INSTALA VEÍCULOS
            // =========================

            setVehicles(veiculos || []);

            // =========================
            // FINALIZA REQUEST
            // =========================

            setLoadProgress(100);

            finishVehiclesRequest(requestId);

            // =========================
            // AINDA EXISTEM VEÍCULOS
            // SENDO CARREGADOS
            // =========================

            const {
                loadingVehicles
            } = useLoadingStore.getState();

            if (loadingVehicles) {
                return;
            }

            // =========================
            // APLICA REALTIME PENDENTE
            // =========================

            flushPendingEvents();

            // =========================
            // INVALIDATE PENDENTE
            // =========================

            if (pendingInvalidate) {

                pendingInvalidate = false;

                loadVehicles(socket);

                return;

            }

            // =========================
            // PRÓXIMA ETAPA
            // =========================

            loadDrivers(socket);

        };

        // =========================
        // ERROR
        // =========================

        const handleError = ({
            requestId
        }) => {

            const isActive = isVehiclesRequestActive(requestId);

            finishVehiclesRequest(requestId);

            if (!isActive) {
                return;
            }

            console.error('Erro ao carregar veículos');

            setLoadProgress(100);

        };

        // =========================
        // APLICA EVENTO REALTIME
        // =========================

        const applyEvent = ({
            type,
            payload
        }) => {

            switch (type) {

                case 'sync':

                    setVehicles(payload || []);

                    break;

                case 'upsert':

                    upsertVehicle(payload);

                    break;

                case 'delete':

                    removeVehicle(payload);

                    break;

                default:
                    break;

            }

        };

        // =========================
        // RECEBE EVENTO REALTIME
        // =========================

        const handleEvent = (
            type,
            payload
        ) => {

            const {
                loadingVehicles
            } = useLoadingStore.getState();

            if (loadingVehicles) {

                pendingVehicleEvents.push({
                    type,
                    payload
                });

                return;
            }

            applyEvent({
                type,
                payload
            });

        };

        // =========================
        // FLUSH REALTIME
        // =========================

        const flushPendingEvents = () => {

            if (!pendingVehicleEvents.length) {
                return;
            }

            const events =
                pendingVehicleEvents.splice(
                    0,
                    pendingVehicleEvents.length
                );

            for (const event of events) {

                applyEvent(event);

            }

        };

        // =========================
        // RELOAD
        // =========================

        const handleReload = () => {

            const {
                loadingVehicles
            } = useLoadingStore.getState();

            if (loadingVehicles) {

                pendingInvalidate = true;

                return;
            }

            loadVehicles(socket);

        };

        // =========================
        // REALTIME
        // =========================

        const handleSync = vehicles => {

            handleEvent('sync', vehicles);

        };

        const handleUpsert = vehicle => {

            handleEvent('upsert', vehicle);

        };

        const handleDelete = vehicleId => {

            handleEvent('delete', vehicleId);

        };

        const handleInvalidate = () => {

            handleReload();

        };

        // =========================
        // DISCONNECT
        // =========================

        const handleDisconnect = () => {

            pendingVehicleEvents.length = 0;

            pendingInvalidate = false;

            clearVehiclesRequests();

        };

        // =========================
        // LISTENERS
        // =========================

        socket.on('disconnect', handleDisconnect);

        socket.on('veiculos:progress', handleProgress);

        socket.on('veiculos:done', handleDone);

        socket.on('veiculos:error', handleError);

        socket.on('veiculos:sync', handleSync);

        socket.on('veiculos:upserted', handleUpsert);

        socket.on('veiculos:deleted', handleDelete);

        socket.on('veiculos:invalidate', handleInvalidate);

        return () => {

            socket.off('disconnect', handleDisconnect);

            socket.off('veiculos:progress', handleProgress);

            socket.off('veiculos:done', handleDone);

            socket.off('veiculos:error', handleError);

            socket.off('veiculos:sync', handleSync);

            socket.off('veiculos:upserted', handleUpsert);

            socket.off('veiculos:deleted', handleDelete);

            socket.off('veiculos:invalidate', handleInvalidate);

            pendingVehicleEvents.length = 0;

            pendingInvalidate = false;

        };

    }, [socket]);

}